import React, {useState} from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import useUser from '@/hooks/useUser';
import {
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PieChartIcon from '@mui/icons-material/PieChart';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// @ts-ignore — JS modules
import {
    AdminHome,
    Alarms,
    DashboardsRoot,
    MonitoringGroups,
    NotificationList,
    PlatformRoot,
    ReportingHome,
    StatisticsHome,
    Trends,
} from '@/routes';
// @ts-ignore — JS module
import {initSelectors} from '@/behavior/init';
// @ts-ignore — JS module
import usePlatform from '@/hooks/usePlatform';
import {openInNewTab} from '@/utils/openInNewTab';
import './BottomNav.css';

/** Monogram icon for external tools that have no good glyph (Grafana, HelpAlarm).
 *  Sized to sit alongside the small MUI icons and inherits the current color. */
const makeLetterIcon = (letter: string): React.FC =>
    function LetterIcon() {
        return (
            <span
                aria-hidden
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '1.25rem',
                    height: '1.25rem',
                    fontSize: '1.05rem',
                    fontWeight: 800,
                    lineHeight: 1,
                    fontFamily: '"Nunito Sans", sans-serif',
                }}
            >
                {letter}
            </span>
        );
    };

const GIcon = makeLetterIcon('G');
const HIcon = makeLetterIcon('H');

interface NavItem {
    key: string;
    label: string;
    Icon: React.ElementType;
    path?: string;
    onClick?: () => void;
    visible?: boolean;
    end?: boolean;
}

const BottomNav: React.FC = () => {
    const platform = usePlatform();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useUser();
    const [moreOpen, setMoreOpen] = useState(false);

    const primaryItems: NavItem[] = [
        {key: 'home', label: 'Home', Icon: HomeIcon, path: PlatformRoot.getLink({platform}, null, false), end: true},
        {key: 'trends', label: 'Trends', Icon: ShowChartIcon, path: Trends.getLink({platform}, null, false)},
        {key: 'alarms', label: 'Alarms', Icon: WarningAmberIcon, path: Alarms.getLink({platform}, null, false)},
        {key: 'dashboards', label: 'Dash.', Icon: DashboardIcon, path: DashboardsRoot.getLink({platform}, null, false)},
        {key: 'notifications', label: 'Notif.', Icon: NotificationsIcon, path: NotificationList.getLink({platform}, null, false)},
    ];

    const moreInternalItems: NavItem[] = [
        {key: 'statistics', label: 'Statistics', Icon: PieChartIcon, path: StatisticsHome.getLink({platform}, null, false)},
        {key: 'reporting', label: 'Reporting', Icon: BusinessCenterIcon, path: ReportingHome.getLink({platform}, null, false)},
        {key: 'monitoring-groups', label: 'Monitoring Groups', Icon: ListAltIcon, path: MonitoringGroups.getLink({platform}, null, false)},
        {key: 'admin', label: 'Admin', Icon: SettingsIcon, path: AdminHome.getLink({platform}, null, false), visible: initSelectors.isAdmin(user)},
    ].filter((item) => item.visible !== false);

    const moreExternalItems: NavItem[] = [
        {
            key: 'grafana',
            label: 'Grafana',
            Icon: GIcon,
            onClick: () =>
                openInNewTab(
                    `https://monit-grafana.cern.ch/d/omhwpE171${platform === 'TEST' ? '-test' : ''}/?orgId=120`,
                ),
        },
        {key: 'rog', label: 'ROG', Icon: WebAssetIcon, onClick: () => openInNewTab('https://rog.cern.ch')},
        {key: 'timber', label: 'Timber', Icon: StackedLineChartIcon, onClick: () => openInNewTab('https://timber.cern.ch')},
        {key: 'helpalarm', label: 'HelpAlarm', Icon: HIcon, onClick: () => openInNewTab('https://helpalarm.cern.ch')},
    ];

    const isPathActive = (path?: string, end?: boolean) => {
        if (!path) return false;
        return end ? location.pathname === path : location.pathname.startsWith(path);
    };

    const activePrimaryIndex = primaryItems.findIndex((it) => isPathActive(it.path, it.end));
    const isMoreActive =
        activePrimaryIndex < 0 && moreInternalItems.some((it) => isPathActive(it.path));
    const indicatorIndex = activePrimaryIndex >= 0 ? activePrimaryIndex : isMoreActive ? primaryItems.length : -1;

    const handleMoreItem = (item: NavItem) => {
        setMoreOpen(false);
        if (item.path) {
            navigate(item.path);
        } else if (item.onClick) {
            item.onClick();
        }
    };

    const itemCount = primaryItems.length + 1; // +1 for the More button

    return (
        <>
            <nav
                className="bottom-nav"
                style={{'--item-count': itemCount} as React.CSSProperties}
                aria-label="Mobile navigation"
                data-tutorial="bottom-nav"
            >
                {indicatorIndex >= 0 && (
                    <div
                        className="bottom-nav-indicator"
                        style={{transform: `translateX(${indicatorIndex * 100}%)`}}
                    />
                )}
                {primaryItems.map((item, index) => {
                    const isActive = index === activePrimaryIndex;
                    return (
                        <NavLink
                            key={item.key}
                            to={item.path!}
                            className={`bottom-nav-item${isActive ? ' bottom-nav-item--active' : ''}`}
                            aria-label={item.label}
                            aria-current={isActive ? 'page' : undefined}
                        >
                            <span className="bottom-nav-icon">
                                <item.Icon fontSize="small"/>
                            </span>
                            <span className="bottom-nav-label">{item.label}</span>
                        </NavLink>
                    );
                })}
                <button
                    type="button"
                    className={`bottom-nav-item${isMoreActive ? ' bottom-nav-item--active' : ''}`}
                    onClick={() => setMoreOpen(true)}
                    aria-label="More"
                >
                    <span className="bottom-nav-icon">
                        <MoreHorizIcon fontSize="small"/>
                    </span>
                    <span className="bottom-nav-label">More</span>
                </button>
            </nav>

            <Drawer
                anchor="bottom"
                open={moreOpen}
                onClose={() => setMoreOpen(false)}
                // The nav itself is z-index 1200, same as the default drawer
                // level — lift the sheet to modal level so it cleanly covers the
                // nav instead of letting iOS paint the nav over its lower items.
                sx={{zIndex: (theme) => theme.zIndex.modal}}
                slotProps={{paper: {className: 'bottom-nav-more-sheet'}}}
            >
                <div className="bottom-nav-more-handle-bar" aria-hidden="true">
                    <span className="bottom-nav-more-handle"/>
                </div>
                <div className="bottom-nav-more-header">
                    <Typography component="h2" className="bottom-nav-more-title">
                        More
                    </Typography>
                    <IconButton
                        onClick={() => setMoreOpen(false)}
                        size="small"
                        aria-label="Close"
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </div>
                <div className="bottom-nav-more-content">
                    <List disablePadding>
                        {moreInternalItems.map((item) => (
                            <ListItemButton
                                key={item.key}
                                onClick={() => handleMoreItem(item)}
                                className="bottom-nav-more-item"
                                selected={isPathActive(item.path)}
                            >
                                <ListItemIcon className="bottom-nav-more-icon">
                                    <item.Icon fontSize="small"/>
                                </ListItemIcon>
                                <ListItemText primary={item.label}/>
                                <ChevronRightIcon className="bottom-nav-more-chevron"/>
                            </ListItemButton>
                        ))}
                    </List>
                    <Divider className="bottom-nav-more-divider"/>
                    <Typography variant="caption" className="bottom-nav-more-section-label">
                        External links
                    </Typography>
                    <List disablePadding>
                        {moreExternalItems.map((item) => (
                            <ListItemButton
                                key={item.key}
                                onClick={() => handleMoreItem(item)}
                                className="bottom-nav-more-item"
                            >
                                <ListItemIcon className="bottom-nav-more-icon">
                                    <item.Icon fontSize="small"/>
                                </ListItemIcon>
                                <ListItemText primary={item.label}/>
                                <ChevronRightIcon className="bottom-nav-more-chevron"/>
                            </ListItemButton>
                        ))}
                    </List>
                    <div className="bottom-nav-more-safe-area"/>
                </div>
            </Drawer>
        </>
    );
};

export default BottomNav;
